import type { Json } from './database.types';

/* =============================================================================
 * The shape a database function promises
 * -----------------------------------------------------------------------------
 * Fifteen screens read a function that returns jsonb. Postgres says jsonb; the
 * generated types say Json, which is every JSON value and so no shape at all;
 * and each caller then states the shape it is expecting:
 *
 *     return data as AttendanceDetail;
 *
 * That assertion is unavoidable — the shape is built inside a SQL function with
 * jsonb_build_object, and nothing on this side can see it. What it should not
 * be is invisible. Written as `as`, it looks exactly like the casts that are
 * merely narrowing something the compiler already knows, and there is no way to
 * find them all afterwards.
 *
 * So they are all written as this instead. `jsonShape` does nothing at runtime.
 * What it gives is a list: every place the application trusts the shape of a
 * jsonb result is one grep away, which is where to start when a SQL function
 * changes what it returns and the screens that read it go quiet rather than
 * loud.
 * ========================================================================== */

/**
 * Reads a jsonb result as the shape the function that produced it builds.
 *
 * The type argument is a claim about SQL, not a check of it. It is only as
 * true as the jsonb_build_object at the other end, and if that changes this
 * will keep compiling — which is the honest position, and the reason for
 * naming it rather than leaving fifteen bare assertions about.
 */
export function jsonShape<T>(value: Json | null | undefined): T {
  return value as unknown as T;
}

/**
 * Hands an object to a jsonb column or argument.
 *
 * The other direction, and a narrower claim. Json admits an object built out of
 * JSON values, so most things pass without this; what does not is anything typed
 * `unknown` — a spreadsheet cell, a computed settlement, a value that came in
 * from outside and was never narrowed. Those are JSON at run time and cannot be
 * shown to be, and this is where that is said.
 *
 * It is not a licence for a Date or a function. Both survive the type check here
 * and neither survives the round trip: a Date arrives back as a string, and a
 * function is dropped without a word.
 */
export function asJson(value: unknown): Json {
  return value as Json;
}
